import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HardHat, ArrowLeft, AlertTriangle } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const H2 = ({ children }) => (
  <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 scroll-mt-24">{children}</h2>
);
const H3 = ({ children }) => (
  <h3 className="text-lg font-bold text-slate-800 mt-6 mb-3">{children}</h3>
);

const TermsPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Terms of Service"
        description="ContractorsLab Terms of Service — the legal agreement governing use of our contractor marketplace."
        path="/terms"
      />
      {/* Header */}
      <header className="bg-slate-900 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black text-white tracking-tight hidden sm:block">CONTRACTORSLAB</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher variant="dark" />
              <Link to="/" className="text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                <ArrowLeft className="w-4 h-4" /> {t('legal.backHome')}
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12">
        <article className="prose prose-slate max-w-none">
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">{t('legal.terms')}</h1>
          <p className="text-slate-500 mb-8">{t('legal.lastUpdated')}: January 19, 2026 · Effective: February 1, 2026</p>

          {/* Summary & Important Notice box */}
          <div className="bg-orange-50 border-l-4 border-orange-600 p-5 mb-8 rounded-sm">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-bold text-slate-900 mb-1">Important — Please Read Carefully</p>
                <p className="text-sm text-slate-700 leading-relaxed m-0">
                  These Terms contain a <strong>binding arbitration clause</strong> (Section 25),
                  a <strong>class-action waiver</strong> (Section 25.5), and a limitation of our liability
                  (Section 14). They affect how disputes are resolved between you and ContractorsLab. By
                  creating an account or using the Service, you represent that you have read, understood,
                  and agreed to these Terms.
                </p>
              </div>
            </div>
          </div>

          <p className="lead text-lg text-slate-700">
            Welcome to ContractorsLab.com ("ContractorsLab," the "Service," or the "Platform"). The
            ContractorsLab.com website, web application, mobile applications, APIs, and all related
            services are <strong>created, owned, and operated by Retention Logic LLC</strong>, a
            limited liability company organized under the laws of the Commonwealth of Puerto Rico
            ("Retention Logic LLC," the "Company," "we," "our," or "us"). References to
            "ContractorsLab" in these Terms refer to the Service; all contractual, legal, and
            commercial obligations described in these Terms are obligations of Retention Logic LLC.
            These Terms of Service ("Terms") form a legally binding agreement between you ("User,"
            "you," or "your") and Retention Logic LLC and govern your access to and use of the
            Service. Please also review our{' '}
            <Link to="/privacy" className="text-orange-600 font-medium">Privacy Policy</Link> and{' '}
            <Link to="/sms-terms" className="text-orange-600 font-medium">SMS Terms</Link>, which are
            incorporated by reference.
          </p>

          <H2>1. Definitions</H2>
          <p>
            Capitalized terms not otherwise defined in these Terms have the following meanings:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>"Account"</strong> means the personal or business profile you create to access the Service.</li>
            <li><strong>"Client"</strong> means a homeowner, property manager, or other end-user who posts jobs seeking Contractor services.</li>
            <li><strong>"Contractor"</strong> means a licensed and insured professional who offers home-improvement, construction, or related services through the Service.</li>
            <li><strong>"Content"</strong> means any text, images, documents, reviews, messages, audio, video, or other materials uploaded, posted, or transmitted through the Service.</li>
            <li><strong>"Lead"</strong> means a job match delivered to a Contractor, including the opportunity to submit a proposal.</li>
            <li><strong>"Proposal"</strong> means a formal bid submitted by a Contractor in response to a Lead, including price, scope, and timeline.</li>
            <li><strong>"Contact Reveal Fee"</strong> means the performance-based fee charged to a Contractor only upon Client selection and mutual contact-info exchange.</li>
            <li><strong>"Wallet"</strong> means the pre-funded account from which Contractor fees are debited.</li>
            <li><strong>"Minimum Balance"</strong> means the balance a Contractor must maintain in their Wallet to remain eligible for Leads (currently $300 USD, subject to change).</li>
            <li><strong>"Verified Contractor"</strong> means a Contractor whose license, insurance, and identity have been verified by ContractorsLab.</li>
            <li><strong>"Applicable Law"</strong> means all federal, state, and local laws, regulations, and ordinances applicable to a given party or transaction.</li>
          </ul>

          <H2>2. Acceptance of Terms; Eligibility</H2>
          <H3>2.1 Acceptance</H3>
          <p>
            By accessing, browsing, registering for, or using the Service, you agree to be bound by these
            Terms, the Privacy Policy, and any supplemental policies referenced herein. If you do not
            agree, you must not use the Service.
          </p>
          <H3>2.2 Age Requirement</H3>
          <p>
            You must be at least eighteen (18) years of age and capable of forming a legally binding
            contract to use the Service. Users under 18 are strictly prohibited. Contractors acting on
            behalf of an entity represent that they have authority to bind that entity.
          </p>
          <H3>2.3 Geographic Restrictions</H3>
          <p>
            The Service is intended for use within the United States of America and its territories. It is
            not directed to, and we do not knowingly collect information from, residents of jurisdictions
            outside the United States. If you access the Service from outside the U.S., you do so at your
            own risk and are responsible for compliance with local laws.
          </p>
          <H3>2.4 Authority to Contract</H3>
          <p>
            If you use the Service on behalf of a business, partnership, LLC, or corporation, you
            represent and warrant that you have the full legal authority to bind that entity to these
            Terms, and that entity is also bound as a "User."
          </p>

          <H2>3. Description of Service</H2>
          <p>
            ContractorsLab is a performance-based, lead-generation marketplace that connects Clients with
            Contractors. ContractorsLab does <strong>not</strong> perform any construction, repair,
            inspection, or home-improvement work. We are a platform provider only. Our Service includes:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Job-posting tools that allow Clients to describe projects, set budgets, and upload photos/documents.</li>
            <li>Lead-matching algorithms that deliver up to three (3) qualified Contractor proposals per job.</li>
            <li>Blind-bidding mechanisms in which Contractors submit Proposals without knowing other bids.</li>
            <li>Verification workflows for Contractor licenses, insurance certificates, and identity documents.</li>
            <li>In-app messaging, contact-reveal workflows, and reputation/review systems.</li>
            <li>Wallet top-ups, payment processing, and transactional recordkeeping via third-party payment processors.</li>
            <li>Educational content, including AI-generated construction tips and industry guides.</li>
            <li>Optional SMS and email notifications (see Section 9).</li>
          </ul>
          <p>
            ContractorsLab does <strong>not</strong> guarantee the quality, safety, legality, pricing, or
            timeliness of any Contractor's work, nor do we guarantee that any Client will hire any
            Contractor. Our role is limited to that of a neutral intermediary.
          </p>

          <H2>4. Account Registration and Security</H2>
          <H3>4.1 Account Creation</H3>
          <p>
            To access most features of the Service, you must register for an Account. You agree to (a)
            provide accurate, current, and complete information, (b) maintain and promptly update such
            information, (c) maintain the security of your password, and (d) immediately notify us of any
            unauthorized use or security breach.
          </p>
          <H3>4.2 Account Types</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Client Account:</strong> For homeowners, landlords, property managers, or other end-users seeking services.</li>
            <li><strong>Contractor Account:</strong> For licensed, insured, and verified professionals offering services.</li>
            <li><strong>Administrator Account:</strong> Reserved for ContractorsLab staff for moderation, verification, and support.</li>
          </ul>
          <H3>4.3 One Account per User</H3>
          <p>
            Each individual or business may maintain only one Account of each type. Multiple accounts used
            to circumvent limits, reviews, or bans will result in immediate and permanent suspension of
            all associated accounts and forfeiture of any Wallet balance.
          </p>
          <H3>4.4 Account Security</H3>
          <p>
            You are solely responsible for all activity on your Account, whether authorized by you or not.
            We strongly recommend enabling multi-factor authentication, using unique strong passwords, and
            logging out of shared devices. ContractorsLab will never ask for your password over email,
            phone, or SMS.
          </p>
          <H3>4.5 Identity Verification</H3>
          <p>
            We may verify your identity at any time by requesting government-issued identification, proof
            of address, business licenses, insurance certificates, or similar documentation. Failure to
            respond within a reasonable period may result in account suspension.
          </p>

          <H2>5. Contractor Obligations and Standards</H2>
          <H3>5.1 Licensing and Insurance</H3>
          <p>Contractors represent, warrant, and agree that they will at all times:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Hold and maintain all contractor licenses, registrations, and certifications required by their state, county, and municipality of operation.</li>
            <li>Maintain general liability insurance with coverage limits of at least $1,000,000 per occurrence, and workers' compensation insurance where required.</li>
            <li>Submit current verification documents to ContractorsLab upon registration and upon any renewal or lapse.</li>
            <li>Notify us within seventy-two (72) hours of any license suspension, revocation, disciplinary action, or insurance lapse.</li>
            <li>Perform all work in accordance with applicable building codes, OSHA standards, and industry best practices.</li>
          </ul>
          <H3>5.2 Wallet Minimum Balance</H3>
          <p>
            Contractors must maintain a Wallet balance of at least $300 USD to remain eligible to receive
            Leads. Accounts falling below the Minimum Balance will be automatically paused — no new Leads
            will be delivered — until the Wallet is replenished. Paused accounts remain fully accessible
            for messaging with existing Clients and managing ongoing jobs.
          </p>
          <H3>5.3 Response Time Standards</H3>
          <p>
            Contractors must respond to Leads within twenty-four (24) hours. Repeated failure to respond
            may result in reduced match priority, temporary suspension, or removal from the Service.
          </p>
          <H3>5.4 Professional Conduct</H3>
          <p>Contractors agree to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Provide truthful, accurate, and complete Proposals, including itemized pricing.</li>
            <li>Honor all quoted prices and timelines absent a mutually agreed written change order.</li>
            <li>Communicate professionally and respectfully in all interactions.</li>
            <li>Never engage in bait-and-switch pricing, fraud, misrepresentation, or unlicensed work.</li>
            <li>Pull all required permits and pass required inspections on behalf of the Client.</li>
            <li>Comply with all local, state, and federal labor, tax, safety, and consumer-protection laws.</li>
          </ul>
          <H3>5.5 Independent Contractor Status</H3>
          <p>
            Contractors are independent third-party businesses and are <strong>not</strong> employees,
            agents, partners, or joint venturers of ContractorsLab. Nothing in these Terms creates any
            employment, franchise, or agency relationship. Contractors are solely responsible for their
            own taxes, tools, insurance, subcontractors, payroll, and compliance with all laws.
          </p>
          <H3>5.6 Tax Reporting</H3>
          <p>
            Contractors are solely responsible for reporting and paying all applicable federal, state, and
            local income, self-employment, sales, and use taxes arising from work performed. ContractorsLab
            does not withhold taxes on payments received directly from Clients. Where legally required
            (e.g., 1099-K thresholds), ContractorsLab will issue tax forms for Wallet transactions; you
            are responsible for providing accurate taxpayer information (W-9 on request).
          </p>

          <H2>6. Client Obligations</H2>
          <p>Clients represent, warrant, and agree that they will:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Be the legal owner of the property where work will be performed, or have documented authority from the owner.</li>
            <li>Provide accurate project descriptions, budgets, and timelines in job postings.</li>
            <li>Respond to Proposals within seven (7) days or expressly decline them.</li>
            <li>Pay selected Contractors directly for all work performed, outside of the ContractorsLab platform (ContractorsLab is <strong>not</strong> an escrow service).</li>
            <li>Leave honest, factual reviews based on actual work experience.</li>
            <li>Not solicit Contractors to circumvent the Service after receiving Leads through ContractorsLab for a period of twelve (12) months after initial contact, except where a Proposal has been formally accepted through the Service.</li>
          </ul>

          <H2>7. Fees, Billing, and Refunds</H2>
          <H3>7.1 Pricing Model</H3>
          <p>
            ContractorsLab operates on a performance-based model: <strong>Clients pay nothing</strong> to
            use the Service. Contractors pay only when a Client selects them and mutual contact
            information is exchanged (the "Contact Reveal Fee"). No monthly subscription, listing fees, or
            per-lead charges apply to Clients.
          </p>
          <H3>7.2 Contact Reveal Fee</H3>
          <p>
            The Contact Reveal Fee is charged to the Contractor's Wallet immediately upon the Client's
            selection of the Contractor and approval of contact-information exchange. The fee is
            calculated as a percentage of the Proposal's stated price (or a fixed amount for certain job
            categories), with the current schedule published at{' '}
            <Link to="/#pricing" className="text-orange-600 font-medium">our pricing page</Link>. Fees are
            non-refundable except in cases of verified platform error or fraud. ContractorsLab reserves
            the right to modify the fee schedule with thirty (30) days' notice.
          </p>
          <H3>7.3 Wallet Top-Ups</H3>
          <p>
            Wallet top-ups are processed through third-party payment processors (currently Stripe, Inc.).
            By topping up, you authorize the processor to charge your selected payment method for the
            designated amount. Wallet funds are denominated in U.S. Dollars and non-transferable between
            Accounts.
          </p>
          <H3>7.4 Refunds and Chargebacks</H3>
          <p>
            Wallet top-ups are refundable in full within seven (7) days of deposit, minus any fees already
            incurred. After seven (7) days, unused Wallet balance may be refunded at ContractorsLab's sole
            discretion. Contact Reveal Fees are refundable only in cases of: (a) Client-verified
            misidentification, (b) duplicate charges, or (c) system error. Initiating a chargeback with
            your card issuer in lieu of contacting support is a breach of these Terms and may result in
            account suspension and a $35 chargeback fee.
          </p>
          <H3>7.5 Taxes on Fees</H3>
          <p>
            All fees are exclusive of taxes. Where sales or use tax applies, you agree to pay all such
            taxes. ContractorsLab may collect and remit applicable sales tax where legally required.
          </p>

          <H2>8. Contact Information Sharing</H2>
          <p>
            ContractorsLab uses a blind-bidding model: Client and Contractor contact details are not
            exchanged until the Client explicitly selects a Contractor and approves the reveal. After
            reveal, both parties may communicate directly via phone, email, or any other channel. You
            agree to use any contact information received solely for the purpose of completing the
            specific project identified, and not for marketing, resale, or any other purpose. Violation
            may result in termination and, where applicable, legal action under anti-spam, anti-trust,
            and non-solicitation laws.
          </p>

          <H2>9. SMS Messaging Consent &amp; Opt-In</H2>
          <p>
            ContractorsLab may offer optional text-message (SMS) communications to keep you informed about
            your account, job activity, and promotional offers. <strong>SMS enrollment is entirely
            optional and is not required to create an account, post a job, receive proposals, or otherwise
            use the Service.</strong>
          </p>
          <H3>9.1 Categories</H3>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>Transactional SMS:</strong> security codes, lead and proposal alerts, contact-reveal notifications, payment receipts, wallet-balance warnings, and other account-event messages.</li>
            <li><strong>Marketing SMS:</strong> promotional offers, new-feature announcements, referral rewards, seasonal campaigns, and educational tips.</li>
          </ul>
          <p>
            You may consent to one category without consenting to the other and may opt out of either at
            any time.
          </p>
          <H3>9.2 How to Opt In</H3>
          <p>
            Opt in by (a) checking the SMS-consent box during registration or in profile settings, (b)
            replying <strong>"START"</strong> to a ContractorsLab short code, or (c) submitting a form
            that clearly discloses SMS consent. You represent that you are the subscriber of the phone
            number provided or have the subscriber's permission.
          </p>
          <H3>9.3 Frequency</H3>
          <p>
            Frequency varies by activity and categories opted into. Marketing messages typically do not
            exceed four (4) per month.
          </p>
          <H3>9.4 Message &amp; Data Rates</H3>
          <p>
            <strong>Message and data rates may apply.</strong> ContractorsLab does not charge for SMS, but
            your carrier's standard rates apply to every message sent or received. Contact your carrier
            for plan details.
          </p>
          <H3>9.5 Opt Out</H3>
          <p>
            Reply <strong>"STOP"</strong> to any ContractorsLab SMS to unsubscribe. One confirmation
            message will follow. For help, reply <strong>"HELP"</strong> or visit our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>. Opting out of
            marketing does not stop critical account-security messages unless you opt out of those
            separately.
          </p>
          <H3>9.6 Not a Condition of Use</H3>
          <p>
            <strong>Consent to SMS is not a condition of purchasing any product or using any Service.</strong>{' '}
            Email and in-app notifications are available as alternatives.
          </p>

          <H2>10. Electronic Communications and Consent to Electronic Records</H2>
          <p>
            By creating an Account, you consent to receive all communications, agreements, notices, and
            disclosures ("Communications") from ContractorsLab electronically, including via email, SMS
            (where enrolled), in-app messages, or posting on the Service. You have the right to receive
            Communications on paper; to request paper copies, contact us via our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>. To receive
            electronic Communications, you must have a modern web browser, an active email account, and —
            for certain documents — PDF-reader software.
          </p>

          <H2>11. User Content and License Grant</H2>
          <H3>11.1 Your Content</H3>
          <p>
            You retain all ownership rights in any Content you submit to the Service (job descriptions,
            photos, reviews, Proposals, etc.). You represent and warrant that you either own or have the
            necessary rights to submit such Content and that it does not infringe any third-party rights.
          </p>
          <H3>11.2 License to ContractorsLab</H3>
          <p>
            By submitting Content, you grant ContractorsLab a worldwide, royalty-free, sublicensable,
            transferable, perpetual (or for as long as legally required) license to use, reproduce,
            modify, adapt, publish, translate, distribute, display, and create derivative works from such
            Content solely for purposes of operating, improving, promoting, and providing the Service.
            This license survives termination of these Terms for Content retained in public archives
            (e.g., published reviews).
          </p>
          <H3>11.3 Feedback</H3>
          <p>
            If you submit feedback, suggestions, or ideas about the Service ("Feedback"), you grant
            ContractorsLab an unrestricted, perpetual, irrevocable, royalty-free license to use the
            Feedback without compensation, attribution, or obligation.
          </p>
          <H3>11.4 Content Moderation</H3>
          <p>
            ContractorsLab reserves the right, but has no obligation, to monitor, edit, remove, or refuse
            to post any Content that violates these Terms or applicable law. We are not responsible for
            User Content and do not endorse any statements therein.
          </p>

          <H2>12. Reviews and Ratings</H2>
          <p>
            Only Clients who have completed a contact-revealed project may leave reviews of a Contractor.
            Reviews must be honest, based on firsthand experience, free of profanity or personal attacks,
            and not contain confidential or protected information. Reviews may be edited for profanity,
            PII, or clear violations of these Terms but will not be altered to change the reviewer's
            sentiment. Contractors may publicly respond to reviews through the platform. Reviews may be
            removed if they violate this Section or applicable law (e.g., defamation).
          </p>

          <H2>13. Prohibited Conduct</H2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-2">
            <li>Violate any law, regulation, or third-party right, including intellectual-property, privacy, publicity, or contract rights.</li>
            <li>Post false, misleading, fraudulent, or deceptive Content.</li>
            <li>Use the Service to defame, stalk, harass, threaten, or discriminate against any person.</li>
            <li>Circumvent the Service to avoid fees or to communicate off-platform before contact reveal.</li>
            <li>Attempt to reverse engineer, decompile, probe, scan, or interfere with any security feature.</li>
            <li>Upload viruses, worms, ransomware, or any other malicious code.</li>
            <li>Use automated scrapers, bots, crawlers, or similar tools to extract data (except as expressly permitted by our robots.txt or API terms).</li>
            <li>Collect personal data of other Users for any purpose other than completing a specific job.</li>
            <li>Submit fake Proposals, fake reviews, or engage in bid-rigging, collusion, or price-fixing.</li>
            <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
            <li>Use the Service in violation of OFAC sanctions lists or any applicable export-control laws.</li>
            <li>Use the Service to engage in unlicensed contracting, unlicensed practice of trades, or activities requiring permits you do not hold.</li>
          </ul>

          <H2>14. Disclaimers of Warranties</H2>
          <p className="uppercase text-sm font-bold text-slate-800 bg-slate-100 p-4 rounded-sm">
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis, without warranties of any
            kind, either express or implied, including but not limited to implied warranties of
            merchantability, fitness for a particular purpose, title, non-infringement, or course of
            dealing. ContractorsLab does not warrant that (a) the Service will be uninterrupted, timely,
            secure, or error-free; (b) defects will be corrected; (c) the Service is free of viruses or
            harmful components; or (d) the results obtained from the Service will be accurate or reliable.
            ContractorsLab is not responsible for the acts, omissions, or work product of any Contractor.
            Some jurisdictions do not allow the exclusion of certain warranties; accordingly, some of the
            above exclusions may not apply to you.
          </p>

          <H2>15. Limitation of Liability</H2>
          <p className="uppercase text-sm font-bold text-slate-800 bg-slate-100 p-4 rounded-sm">
            To the maximum extent permitted by Applicable Law, in no event shall ContractorsLab, its
            officers, directors, employees, agents, affiliates, or licensors be liable for any indirect,
            incidental, special, consequential, exemplary, or punitive damages — including loss of
            profits, goodwill, data, or property damage — arising out of or related to your use of the
            Service, even if advised of the possibility of such damages. Our aggregate liability for all
            claims arising under these Terms shall not exceed the greater of (a) one hundred U.S. dollars
            ($100) or (b) the total amount of fees paid by you to ContractorsLab during the twelve (12)
            months preceding the event giving rise to the claim. Some jurisdictions do not allow the
            limitation of certain damages; in such jurisdictions liability is limited to the fullest
            extent permitted by law.
          </p>

          <H2>16. Indemnification</H2>
          <p>
            You agree to defend, indemnify, and hold harmless ContractorsLab and its officers, directors,
            employees, agents, affiliates, and licensors from and against any and all claims, damages,
            obligations, losses, liabilities, costs, and expenses (including reasonable attorneys' fees)
            arising from: (a) your use of and access to the Service; (b) your breach of these Terms; (c)
            your violation of any third-party right; (d) any claim that your Content caused damage to a
            third party; (e) for Contractors, any work performed, permit not pulled, injury, or property
            damage arising from your services. This indemnification obligation survives termination.
          </p>

          <H2>17. Intellectual Property of ContractorsLab</H2>
          <p>
            The Service, including all software, designs, text, graphics, logos, icons, audio, video,
            AI-generated educational content, trade names, service marks, and trademarks ("Marks"), is
            owned by or licensed to ContractorsLab and is protected by U.S. and international copyright,
            trademark, patent, and other intellectual-property laws. Nothing in these Terms grants you any
            license to use the Marks without our prior written permission. You may not frame, mirror, or
            scrape the Service, remove proprietary notices, or use the Marks as part of a metatag or
            sponsored listing without permission.
          </p>

          <H2>18. DMCA and Copyright Policy</H2>
          <p>
            ContractorsLab respects intellectual-property rights and will respond to clear notices of
            alleged copyright infringement that comply with the Digital Millennium Copyright Act
            ("DMCA"). To submit a takedown notice, contact our designated agent through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link> (select
            "Legal / Compliance") and include: (a) your contact information; (b) identification of the
            copyrighted work; (c) identification of the allegedly infringing material; (d) a statement of
            good-faith belief that the use is not authorized; (e) a statement under penalty of perjury
            that the information is accurate; and (f) your physical or electronic signature. Counter-
            notices may be submitted in response to a takedown under 17 U.S.C. § 512(g). Repeat infringers
            will be terminated.
          </p>

          <H2>19. Third-Party Services and Links</H2>
          <p>
            The Service integrates with or links to third-party services, including payment processors
            (Stripe), cloud storage (AWS S3), authentication providers (Google via Emergent), SMS
            carriers, email providers, and mapping services. These third-party services are governed by
            their own terms and privacy policies. ContractorsLab is not responsible for the content,
            performance, or practices of any third-party service. Your use of third-party services is at
            your own risk.
          </p>

          <H2>20. Beta Features</H2>
          <p>
            We may occasionally offer features labeled "Beta," "Preview," or "Early Access." Such
            features are provided AS-IS, may be modified or discontinued at any time, and may not be
            covered by service-level commitments, if any. Feedback on beta features is governed by
            Section 11.3.
          </p>

          <H2>21. Anti-Discrimination and Fair Housing</H2>
          <p>
            ContractorsLab is committed to a discrimination-free marketplace. Users may not discriminate
            on the basis of race, color, religion, sex (including gender identity and sexual orientation),
            national origin, age, disability, familial status, marital status, veteran status, or any
            other protected characteristic under federal, state, or local law. Violations may result in
            termination and referral to appropriate authorities, including fair-housing enforcement
            agencies.
          </p>

          <H2>22. Accessibility</H2>
          <p>
            ContractorsLab is committed to providing a Service accessible to people with disabilities,
            consistent with the Americans with Disabilities Act ("ADA") and the Web Content Accessibility
            Guidelines (WCAG) 2.1 Level AA. If you encounter an accessibility barrier, please notify us
            through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>{' '}
            so we can address it promptly.
          </p>

          <H2>23. Suspension and Termination</H2>
          <H3>23.1 Termination by You</H3>
          <p>
            You may terminate your Account at any time by using the in-app delete feature or submitting a
            request through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>.
            Termination does not entitle you to a refund except as provided in Section 7.4.
          </p>
          <H3>23.2 Termination by Us</H3>
          <p>
            We may suspend or terminate your access to the Service, with or without notice and with or
            without cause, at our sole discretion, including but not limited to: (a) breach of these
            Terms; (b) suspected fraud, abuse, or illegal activity; (c) license or insurance lapse; (d)
            repeated negative reviews; (e) chargebacks; (f) government requests or legal process.
          </p>
          <H3>23.3 Effect of Termination</H3>
          <p>
            Upon termination: (a) your Account and ability to post or receive Leads cease immediately; (b)
            unused Wallet balances may be returned subject to Section 7.4; (c) Content you have posted
            (reviews, job history) may remain on the Service; (d) Sections that by their nature should
            survive (e.g., 11, 12, 14, 15, 16, 17, 18, 24, 25) will survive.
          </p>

          <H2>24. Modifications to Terms and Service</H2>
          <p>
            We may modify these Terms from time to time. Material changes will be notified through in-app
            notice, email, or by updating the "Last Updated" date at the top of this page at least thirty
            (30) days before taking effect (except for legal or security changes, which may take effect
            immediately). Your continued use of the Service after the effective date constitutes
            acceptance. We may also modify, suspend, or discontinue any part of the Service at any time
            without liability.
          </p>

          <H2>25. Dispute Resolution; Binding Arbitration; Class Action Waiver</H2>
          <H3>25.1 Informal Resolution</H3>
          <p>
            Before initiating any formal dispute, you agree to first contact us through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link> (select
            "Dispute or Complaint") and attempt to resolve the dispute informally in good faith for at
            least sixty (60) days.
          </p>
          <H3>25.2 Binding Arbitration</H3>
          <p className="uppercase text-sm font-bold text-slate-800 bg-slate-100 p-4 rounded-sm">
            Except for claims for injunctive relief or intellectual-property infringement, any dispute
            arising out of or relating to these Terms or the Service shall be resolved by final and
            binding arbitration administered by the American Arbitration Association ("AAA") under its
            Consumer Arbitration Rules. The arbitration will be conducted in English by a single
            arbitrator, in the state of your residence (or, if a business entity, in the state of its
            principal place of business). Judgment on the award may be entered in any court of competent
            jurisdiction.
          </p>
          <H3>25.3 Arbitration Fees</H3>
          <p>
            ContractorsLab will pay AAA filing and arbitration fees in excess of any applicable court
            filing fee, except where your claim is found to be frivolous.
          </p>
          <H3>25.4 Opt-Out of Arbitration</H3>
          <p>
            You may opt out of this arbitration provision by sending a written notice to our designated
            agent, through our <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>{' '}
            (select "Legal / Compliance"), within thirty (30) days of first accepting these Terms. Your
            notice must include your full name, Account email, and a statement that you decline
            arbitration.
          </p>
          <H3>25.5 Class Action Waiver</H3>
          <p className="uppercase text-sm font-bold text-slate-800 bg-slate-100 p-4 rounded-sm">
            You and ContractorsLab each agree that any dispute resolution proceedings will be conducted
            only on an individual basis and not in a class, consolidated, or representative action. If for
            any reason a claim proceeds in court rather than in arbitration, we each waive any right to a
            jury trial.
          </p>
          <H3>25.6 Exceptions</H3>
          <p>
            Notwithstanding the above, either party may bring an individual action in small-claims court
            for disputes within that court's jurisdiction, and nothing in this Section prevents either
            party from seeking injunctive or other equitable relief for intellectual-property
            infringement or misuse of confidential information.
          </p>

          <H2>26. Governing Law and Venue</H2>
          <p>
            These Terms are governed by and construed in accordance with the laws of the
            <strong> Commonwealth of Puerto Rico</strong> and applicable federal laws of the United
            States of America, without regard to its conflict-of-laws provisions. For matters not
            subject to arbitration, the parties submit to the exclusive jurisdiction of the
            Commonwealth Court of First Instance of Puerto Rico (Tribunal de Primera Instancia) and
            the United States District Court for the District of Puerto Rico, both sitting in
            San Juan, Puerto Rico. The U.N. Convention on Contracts for the International Sale of
            Goods does not apply.
          </p>

          <H2>27. Force Majeure</H2>
          <p>
            Neither party is liable for failure or delay in performance due to causes beyond its
            reasonable control, including acts of God, natural disasters, epidemics, pandemics, war,
            terrorism, civil unrest, labor disputes, internet or telecommunications outages,
            cyber-attacks, or government action.
          </p>

          <H2>28. Assignment</H2>
          <p>
            You may not assign or transfer these Terms, by operation of law or otherwise, without our
            prior written consent. ContractorsLab may freely assign or transfer these Terms without
            restriction, including in connection with a merger, acquisition, reorganization, or sale of
            assets. Any attempted assignment in violation of this Section is void.
          </p>

          <H2>29. Entire Agreement; Severability; No Waiver</H2>
          <p>
            These Terms, together with the Privacy Policy, SMS Terms, and any policies expressly
            incorporated by reference, constitute the entire agreement between you and ContractorsLab
            regarding the Service and supersede all prior or contemporaneous communications, proposals,
            or agreements. If any provision is held invalid or unenforceable, that provision will be
            enforced to the maximum extent permissible, and the remaining provisions will remain in full
            force. Our failure to enforce any right or provision is not a waiver of that right or
            provision.
          </p>

          <H2>30. California Consumer Notice</H2>
          <p>
            California residents: please review our dedicated{' '}
            <Link to="/california-privacy" className="text-orange-600 font-medium">California Consumer Privacy Notice</Link>{' '}
            for a full description of your rights under the CCPA/CPRA, including rights to know, delete,
            correct, port, opt out of sale/sharing, and limit use of sensitive personal information.
          </p>
          <p>
            Under California Civil Code Section 1789.3, California residents are entitled to notice that
            the Complaint Assistance Unit of the Division of Consumer Services of the California
            Department of Consumer Affairs may be contacted in writing at 1625 North Market Blvd., Suite
            N 112, Sacramento, CA 95834, or by telephone at (800) 952-5210.
          </p>

          <H2>31. Export Controls</H2>
          <p>
            You represent and warrant that you (a) are not located in, under the control of, or a
            national or resident of any country subject to U.S. embargo; (b) are not on any U.S.
            government list of restricted end-users; and (c) will not export, re-export, or transfer the
            Service in violation of U.S. export laws.
          </p>

          <H2>32. Government Users</H2>
          <p>
            The Service and its documentation are "commercial items" as defined in FAR 2.101. If
            acquired by or on behalf of any U.S. government agency, the Service is provided with only
            those rights set forth in these Terms.
          </p>

          <H2>33. Language of Agreement</H2>
          <p>
            These Terms are drafted in English. Any translation is provided for convenience only. In case
            of conflict, the English version controls.
          </p>

          <H2>34. Contact Information</H2>
          <p>
            For questions about these Terms, please reach us through our{' '}
            <Link to="/contact" className="text-orange-600 font-medium">Contact page</Link>. Select the{' '}
            <strong>Legal / Compliance</strong> department for the fastest routing. For DMCA notices,
            select the same category and include the information required in Section 18.
          </p>

          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-sm text-slate-500">
              By using ContractorsLab, you acknowledge that you have read, understood, and agreed to be
              bound by these Terms of Service.
            </p>
          </div>
        </article>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-600 rounded-sm flex items-center justify-center">
                <HardHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-black text-white">CONTRACTORSLAB</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <Link to="/terms" className="hover:text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/california-privacy" className="hover:text-white">CA Privacy</Link>
              <Link to="/sms-terms" className="hover:text-white">SMS Terms</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
